import './App.css'
import { Toaster } from "@/components/ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import VisualEditAgent from '@/lib/VisualEditAgent'
import NavigationTracker from '@/lib/NavigationTracker'
import { pagesConfig } from './pages.config'
import { BrowserRouter as Router, Route, Routes, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useLocalizedRouteMeta } from '@/hooks/useLocalizedRouteMeta';
import { useEffect, Suspense } from 'react';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import { LayoutProvider } from '@/lib/LayoutContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import OrthocarePresentationRent from './pages/OrthocarePresentationRent';
import GestosAgeisPresentationRent from './pages/GestosAgeisPresentationRent';
import OgImagePreview from './pages/OgImagePreview';
import { lazy } from 'react';
const SearchLogs = lazy(() => import('./pages/SearchLogs'));
const StripeSuccess = lazy(() => import('./pages/StripeSuccess'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const FAQPublic = lazy(() => import('./pages/FAQPublic'));
const DistrictSEOPage = lazy(() => import('./pages/DistrictSEOPage'));
const CountySEOPage = lazy(() => import('./pages/CountySEOPage'));
const MobilityHubSEOPage = lazy(() => import('./pages/MobilityHubSEOPage'));
const TravelHubSEOPage = lazy(() => import('./pages/TravelHubSEOPage'));
const CityAccessibilitySEOPage = lazy(() => import('./pages/CityAccessibilitySEOPage'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const Reviews = lazy(() => import('./pages/Reviews'));
const EquipmentSEOPage = lazy(() => import('./pages/EquipmentSEOPage'));
const CategorySEOPage = lazy(() => import('./pages/CategorySEOPage'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Terms = lazy(() => import('./pages/Terms'));
const Cookies = lazy(() => import('./pages/Cookies'));
const RentalGuide = lazy(() => import('./pages/RentalGuide'));
import CookieConsent from '@/components/layout/CookieConsent';
import PartnerReferralCapture from '@/components/layout/PartnerReferralCapture';
import PartnerReferralRedirect from '@/components/layout/PartnerReferralRedirect';
const PartnersPage = lazy(() => import('./pages/PartnersPage'));
const PartnersManagement = lazy(() => import('./pages/PartnersManagement'));
const ShopCategorySEOPage = lazy(() => import('./pages/ShopCategorySEOPage'));
const ShopProductSEOPage = lazy(() => import('./pages/ShopProductSEOPage'));
const ShopBrandSEOPage = lazy(() => import('./pages/ShopBrandSEOPage'));
const Marketing = lazy(() => import('./pages/Marketing'));
const InstagramAnalytics = lazy(() => import('./pages/InstagramAnalytics'));
const TrafficReport = lazy(() => import('./pages/TrafficReport'));
const AluguerCadeiraDeRodas = lazy(() => import('./pages/AluguerCadeiraDeRodas'));
const AluguerCamaArticulada = lazy(() => import('./pages/AluguerCamaArticulada'));
const AluguerAndarilhos = lazy(() => import('./pages/AluguerAndarilhos'));
const AluguerScooterMobilidade = lazy(() => import('./pages/AluguerScooterMobilidade'));
const AluguerCadeiraDeRodasEletrica = lazy(() => import('./pages/AluguerCadeiraDeRodasEletrica'));
const OrtopediaBenfica = lazy(() => import('./pages/OrtopediaBenfica'));
const OrtopediaLisboa = lazy(() => import('./pages/OrtopediaLisboa'));
const OrtopediaJunqueira = lazy(() => import('./pages/OrtopediaJunqueira'));
const ComprarCadeiraDeRodas = lazy(() => import('./pages/ComprarCadeiraDeRodas'));
const ComprarCamaArticulada = lazy(() => import('./pages/ComprarCamaArticulada'));
const ComprarAndarilhos = lazy(() => import('./pages/ComprarAndarilhos'));
const ComprarScooterMobilidade = lazy(() => import('./pages/ComprarScooterMobilidade'));
const VisitOurStores = lazy(() => import('./pages/VisitOurStores'));
const LojaOrtopediaLisboa = lazy(() => import('./pages/LojaOrtopediaLisboa'));
const LojaOrtopediaBenfica = lazy(() => import('./pages/LojaOrtopediaBenfica'));
const LojaOrtopediaJunqueira = lazy(() => import('./pages/LojaOrtopediaJunqueira'));
const OrtopediaRestelo = lazy(() => import('./pages/OrtopediaRestelo'));
const OrtopediaPortugal = lazy(() => import('./pages/OrtopediaPortugal'));
const AluguerMaterialOrtopedicoLisboa = lazy(() => import('./pages/AluguerMaterialOrtopedicoLisboa'));
const MelhorLojaOrtopediaLisboa = lazy(() => import('./pages/MelhorLojaOrtopediaLisboa'));
const DriverDeliveryInfo = lazy(() => import('./pages/DriverDeliveryInfo'));
const ReservationSummary = lazy(() => import('./pages/ReservationSummary'));
const DriverWeeklySchedule = lazy(() => import('./pages/DriverWeeklySchedule'));
const PendingReservations = lazy(() => import('./pages/PendingReservations'));
const PushNotifications = lazy(() => import('./pages/PushNotifications'));
const RentalSearchPerformance = lazy(() => import('./pages/RentalSearchPerformance'));
const EquipmentSearchPerformance = lazy(() => import('./pages/EquipmentSearchPerformance'));
const MobilitySearchPerformance = lazy(() => import('./pages/MobilitySearchPerformance'));
const ProductIndexStatus = lazy(() => import('./pages/ProductIndexStatus'));
const Connect = lazy(() => import('./pages/Connect'));
const ContractView = lazy(() => import('./pages/ContractView'));
const POS = lazy(() => import('./pages/POS'));
const POSSlideshowManagement = lazy(() => import('./pages/POSSlideshowManagement'));
const RegionalHubSEOPage = lazy(() => import('./pages/RegionalHubSEOPage'));
const BuyCountySEOPage = lazy(() => import('./pages/BuyCountySEOPage'));

const { Pages, Layout, mainPage } = pagesConfig;
const mainPageKey = mainPage ?? Object.keys(Pages)[0];
// gestosageis.com uses the LandingPage as its independent homepage (no redirect to /gestosageis)
const __host = typeof window !== 'undefined' ? window.location.hostname : '';
const __isGestosAgeisCom = __host === 'gestosageis.com' || __host === 'www.gestosageis.com';
const __isOrthocareDomain = __host === 'orthocare.pt' || __host === 'orthocarerent.pt' || __host === 'orthocarerent.com';
const __isGestosAgeisPt = __host === 'gestosageis.pt';
const __isGestosAgeisStore = __host === 'gestosageis.store';
const __isGestosAgeisOnline = __host === 'gestosageis.online' || __host === 'www.gestosageis.online';
const __isGestosBase44App = __host === 'gestos.base44.app';
// Each domain serves its homepage directly at "/" (no JS redirect) so Google
// indexes the root domain — not a subpath — and all 6 domains rank independently.
const MainPage = __isGestosAgeisCom ? Pages.LandingPage
  : __isOrthocareDomain ? OrthocarePresentationRent
  : __isGestosAgeisPt ? GestosAgeisPresentationRent
  : __isGestosAgeisStore ? Pages.Shop
  : __isGestosAgeisOnline ? POS
  : __isGestosBase44App ? Pages.Welcome
  : (mainPageKey ? Pages[mainPageKey] : <></>);
const mainPageName = __isGestosBase44App ? 'Welcome' : mainPageKey;

const PageFallback = () => (
  <div className="fixed inset-0 flex items-center justify-center">
    <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
  </div>
);

const LayoutWrapper = ({ children, currentPageName }) => Layout ? (
  <Suspense fallback={<PageFallback />}>
    <Layout currentPageName={currentPageName}>{children}</Layout>
  </Suspense>
) : <>{children}</>;

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, isAuthenticated, navigateToLogin } = useAuth();

  // Check domain-based redirects
  const hostname = window.location.hostname;

  // Localized SEO routes must declare the matching <html lang> — Google was
  // reading lang="pt-PT" on the Italian and French pages. Declared at the app
  // root so the effect runs after the page's own effects and wins.
  const { pathname } = useLocation();
  useLocalizedRouteMeta(pathname);

  // gestosageis.online is an internal POS counter display — never index it.
  // Must run before any early return so the hook is always called (Rules of Hooks).
  useEffect(() => {
    if (hostname === 'gestosageis.online' || hostname === 'www.gestosageis.online') {
      let meta = document.querySelector('meta[name="robots"]');
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute('name', 'robots');
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', 'noindex, nofollow');
    }
  }, [hostname]);

  // Redirect www to non-www (critical for SEO — prevents duplicate content / split authority)
  if (hostname === 'www.orthocare.pt') {
    window.location.replace('https://orthocare.pt' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  if (hostname === 'www.gestosageis.pt') {
    window.location.replace('https://gestosageis.pt' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  // Redirect www to non-www for gestosageis.com (independent domain, avoids duplicate content)
  if (hostname === 'www.gestosageis.com') {
    window.location.replace('https://gestosageis.com' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  if (hostname === 'www.gestosageis.store') {
    window.location.replace('https://gestosageis.store' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  // Redirect www to non-www for gestosageis.online (POS domain)
  if (hostname === 'www.gestosageis.online') {
    window.location.replace('https://gestosageis.online' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  // Redirect www to non-www for orthocarerent domains (SEO)
  if (hostname === 'www.orthocarerent.pt') {
    window.location.replace('https://orthocarerent.pt' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }
  if (hostname === 'www.orthocarerent.com') {
    window.location.replace('https://orthocarerent.com' + window.location.pathname + window.location.search + window.location.hash);
    return null;
  }

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Public routes (landing pages, shop, SEO pages, etc.) must keep rendering
      // even when a stale/expired token is present in the browser. Only gate
      // non-public (backoffice) routes behind login.
      if (!isPublicRoute(window.location.pathname)) {
        navigateToLogin();
        return null;
      }
    }
  }

  // Render the main app
  return (
    <>
    <Suspense fallback={<PageFallback />}>
    <Routes>
      <Route path="/" element={<LayoutWrapper currentPageName={mainPageName}><MainPage /></LayoutWrapper>} />
      <Route path="/Home" element={<Navigate to="/" replace />} />
      {Object.entries(Pages).map(([name, Component]) => (
        <Route key={name} path={`/${name}`} element={<LayoutWrapper currentPageName={name}><Component /></LayoutWrapper>} />
      ))}
      <Route path="/CustomerPortal" element={<Navigate to="/" replace />} />
      <Route path="/start" element={<OrthocarePresentationRent />} />
      <Route path="/og-preview" element={<LayoutWrapper currentPageName="OgImagePreview"><OgImagePreview /></LayoutWrapper>} />
      <Route path="/gestosageis" element={<GestosAgeisPresentationRent />} />
      <Route path="/parceiros" element={<PartnersPage />} />
      <Route path="/PartnersManagement" element={<LayoutWrapper currentPageName="PartnersManagement"><PartnersManagement /></LayoutWrapper>} />
      <Route path="/Marketing" element={<LayoutWrapper currentPageName="Marketing"><Marketing /></LayoutWrapper>} />
      <Route path="/InstagramAnalytics" element={<LayoutWrapper currentPageName="InstagramAnalytics"><InstagramAnalytics /></LayoutWrapper>} />
      <Route path="/TrafficReport" element={<LayoutWrapper currentPageName="TrafficReport"><TrafficReport /></LayoutWrapper>} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/cookies" element={<Cookies />} />
      <Route path="/guia" element={<RentalGuide />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/faq" element={<FAQPublic />} />
      <Route path="/about" element={<AboutUs />} />
      <Route path="/SearchResults" element={<SearchResults />} />
      <Route path="/Reviews" element={<Reviews />} />
      <Route path="/obrigado" element={<ThankYou />} />
      <Route path="/aluguer/categoria/:category" element={<CategorySEOPage />} />
      <Route path="/loja/:categorySlug" element={<ShopCategorySEOPage />} />
      <Route path="/loja/produto/:slugId" element={<ShopProductSEOPage />} />
      <Route path="/loja/marca/:brandSlug" element={<ShopBrandSEOPage />} />
      {/* Multilingual shop subdirectories (gestosageis.com) — PT is the unprefixed default */}
      <Route path="/en/shop/:categorySlug" element={<ShopCategorySEOPage lang="en" />} />
      <Route path="/en/shop/product/:slugId" element={<ShopProductSEOPage lang="en" />} />
      <Route path="/en/shop/brand/:brandSlug" element={<ShopBrandSEOPage lang="en" />} />
      <Route path="/es/tienda/:categorySlug" element={<ShopCategorySEOPage lang="es" />} />
      <Route path="/es/tienda/producto/:slugId" element={<ShopProductSEOPage lang="es" />} />
      <Route path="/es/tienda/marca/:brandSlug" element={<ShopBrandSEOPage lang="es" />} />
      <Route path="/fr/boutique/:categorySlug" element={<ShopCategorySEOPage lang="fr" />} />
      <Route path="/fr/boutique/produit/:slugId" element={<ShopProductSEOPage lang="fr" />} />
      <Route path="/fr/boutique/marque/:brandSlug" element={<ShopBrandSEOPage lang="fr" />} />
      <Route path="/de/laden/:categorySlug" element={<ShopCategorySEOPage lang="de" />} />
      <Route path="/de/laden/produkt/:slugId" element={<ShopProductSEOPage lang="de" />} />
      <Route path="/de/laden/marke/:brandSlug" element={<ShopBrandSEOPage lang="de" />} />
      <Route path="/aluguer/categoria/:category/:county" element={<CategorySEOPage />} />
      <Route path="/aluguer/distrito/:district" element={<DistrictSEOPage />} />
      <Route path="/aluguer/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/aluguer/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/en/rent/district/:district" element={<DistrictSEOPage />} />
      <Route path="/en/rent/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/en/rent/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/es/alquilar/distrito/:district" element={<DistrictSEOPage />} />
      <Route path="/es/alquilar/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/es/alquilar/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/fr/louer/district/:district" element={<DistrictSEOPage />} />
      <Route path="/fr/louer/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/fr/louer/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/de/mieten/bezirk/:district" element={<DistrictSEOPage />} />
      <Route path="/de/mieten/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/de/mieten/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/it/noleggiare/distretto/:district" element={<DistrictSEOPage />} />
      <Route path="/it/noleggiare/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/it/noleggiare/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/nl/huren/district/:district" element={<DistrictSEOPage />} />
      <Route path="/nl/huren/:equipment" element={<EquipmentSEOPage />} />
      <Route path="/nl/huren/:equipment/:county" element={<EquipmentSEOPage />} />
      <Route path="/aluguer/concelho/:county" element={<CountySEOPage />} />
      <Route path="/compra-material-ortopedico/:county" element={<BuyCountySEOPage />} />
      <Route path="/compra/:county" element={<BuyCountySEOPage />} />
      <Route path="/en/rent/county/:county" element={<CountySEOPage />} />
      <Route path="/es/alquilar/concelho/:county" element={<CountySEOPage />} />
      <Route path="/fr/louer/commune/:county" element={<CountySEOPage />} />
      <Route path="/de/mieten/kreis/:county" element={<CountySEOPage />} />
      <Route path="/it/noleggiare/comune/:county" element={<CountySEOPage />} />
      <Route path="/nl/huren/gemeente/:county" element={<CountySEOPage />} />
      <Route path="/aluguer-cadeira-de-rodas" element={<LayoutWrapper currentPageName="AluguerCadeiraDeRodas"><AluguerCadeiraDeRodas /></LayoutWrapper>} />
      <Route path="/aluguer-cama-articulada" element={<LayoutWrapper currentPageName="AluguerCamaArticulada"><AluguerCamaArticulada /></LayoutWrapper>} />
      <Route path="/aluguer-andarilhos" element={<LayoutWrapper currentPageName="AluguerAndarilhos"><AluguerAndarilhos /></LayoutWrapper>} />
      <Route path="/aluguer-scooter-mobilidade" element={<LayoutWrapper currentPageName="AluguerScooterMobilidade"><AluguerScooterMobilidade /></LayoutWrapper>} />
      <Route path="/aluguer-cadeira-de-rodas-eletrica" element={<LayoutWrapper currentPageName="AluguerCadeiraDeRodasEletrica"><AluguerCadeiraDeRodasEletrica /></LayoutWrapper>} />
      <Route path="/ortopedia-benfica" element={<LayoutWrapper currentPageName="OrtopediaBenfica"><OrtopediaBenfica /></LayoutWrapper>} />
      <Route path="/ortopedia-lisboa" element={<LayoutWrapper currentPageName="OrtopediaLisboa"><OrtopediaLisboa /></LayoutWrapper>} />
      <Route path="/ortopedia-junqueira" element={<LayoutWrapper currentPageName="OrtopediaJunqueira"><OrtopediaJunqueira /></LayoutWrapper>} />
      <Route path="/comprar-cadeira-de-rodas" element={<LayoutWrapper currentPageName="ComprarCadeiraDeRodas"><ComprarCadeiraDeRodas /></LayoutWrapper>} />
      <Route path="/comprar-cama-articulada" element={<LayoutWrapper currentPageName="ComprarCamaArticulada"><ComprarCamaArticulada /></LayoutWrapper>} />
      <Route path="/comprar-andarilhos" element={<LayoutWrapper currentPageName="ComprarAndarilhos"><ComprarAndarilhos /></LayoutWrapper>} />
      <Route path="/comprar-scooter-mobilidade" element={<LayoutWrapper currentPageName="ComprarScooterMobilidade"><ComprarScooterMobilidade /></LayoutWrapper>} />
      <Route path="/lojas" element={<LayoutWrapper currentPageName="VisitOurStores"><VisitOurStores /></LayoutWrapper>} />
      <Route path="/loja-ortopedia-lisboa" element={<LayoutWrapper currentPageName="LojaOrtopediaLisboa"><LojaOrtopediaLisboa /></LayoutWrapper>} />
      <Route path="/loja-ortopedia-benfica" element={<LayoutWrapper currentPageName="LojaOrtopediaBenfica"><LojaOrtopediaBenfica /></LayoutWrapper>} />
      <Route path="/loja-ortopedia-junqueira" element={<LayoutWrapper currentPageName="LojaOrtopediaJunqueira"><LojaOrtopediaJunqueira /></LayoutWrapper>} />
      <Route path="/ortopedia-restelo" element={<LayoutWrapper currentPageName="OrtopediaRestelo"><OrtopediaRestelo /></LayoutWrapper>} />
      <Route path="/ortopedia" element={<LayoutWrapper currentPageName="OrtopediaPortugal"><OrtopediaPortugal /></LayoutWrapper>} />
      <Route path="/aluguer-material-ortopedico-lisboa" element={<LayoutWrapper currentPageName="AluguerMaterialOrtopedicoLisboa"><AluguerMaterialOrtopedicoLisboa /></LayoutWrapper>} />
      <Route path="/melhor-loja-ortopedia-lisboa" element={<LayoutWrapper currentPageName="MelhorLojaOrtopediaLisboa"><MelhorLojaOrtopediaLisboa /></LayoutWrapper>} />
      <Route path="/entrega/:reservationId" element={<DriverDeliveryInfo />} />
      <Route path="/DriverWeeklySchedule" element={<LayoutWrapper currentPageName="DriverWeeklySchedule"><DriverWeeklySchedule /></LayoutWrapper>} />
      <Route path="/PendingReservations" element={<LayoutWrapper currentPageName="PendingReservations"><PendingReservations /></LayoutWrapper>} />
      <Route path="/PushNotifications" element={<LayoutWrapper currentPageName="PushNotifications"><PushNotifications /></LayoutWrapper>} />
      <Route path="/RentalSearchPerformance" element={<LayoutWrapper currentPageName="RentalSearchPerformance"><RentalSearchPerformance /></LayoutWrapper>} />
      <Route path="/EquipmentSearchPerformance" element={<LayoutWrapper currentPageName="EquipmentSearchPerformance"><EquipmentSearchPerformance /></LayoutWrapper>} />
      <Route path="/MobilitySearchPerformance" element={<LayoutWrapper currentPageName="MobilitySearchPerformance"><MobilitySearchPerformance /></LayoutWrapper>} />
      <Route path="/ProductIndexStatus" element={<LayoutWrapper currentPageName="ProductIndexStatus"><ProductIndexStatus /></LayoutWrapper>} />
      <Route path="/connect" element={<Connect />} />
      <Route path="/POS" element={<POS />} />
      <Route path="/pos" element={<POS />} />
      <Route path="/POSSlideshowManagement" element={<LayoutWrapper currentPageName="POSSlideshowManagement"><POSSlideshowManagement /></LayoutWrapper>} />
      <Route path="/contrato/:contractId" element={<ContractView />} />
      <Route path="/r/:reservationId" element={<ReservationSummary />} />
      <Route path="/mobilidade" element={<MobilityHubSEOPage />} />
      <Route path="/en/mobility" element={<MobilityHubSEOPage />} />
      <Route path="/aluguer/viagens/:spot" element={<TravelHubSEOPage />} />
      <Route path="/en/rent/travel/:spot" element={<TravelHubSEOPage />} />
      {/* Regional rental hubs — high-authority SEO pages targeting inbound travelers */}
      <Route path="/aluguer-cadeira-de-rodas-lisboa" element={<RegionalHubSEOPage region="lisbon" />} />
      <Route path="/aluguer-cadeira-de-rodas-algarve" element={<RegionalHubSEOPage region="algarve" />} />
      <Route path="/aluguer-cadeira-de-rodas-porto" element={<RegionalHubSEOPage region="porto" />} />
      <Route path="/en/wheelchair-rental-lisbon" element={<RegionalHubSEOPage region="lisbon" />} />
      <Route path="/en/wheelchair-rental-algarve" element={<RegionalHubSEOPage region="algarve" />} />
      <Route path="/en/wheelchair-rental-porto" element={<RegionalHubSEOPage region="porto" />} />
      <Route path="/es/alquiler-silla-de-ruedas-lisboa" element={<RegionalHubSEOPage region="lisbon" />} />
      <Route path="/es/alquiler-silla-de-ruedas-algarve" element={<RegionalHubSEOPage region="algarve" />} />
      <Route path="/es/alquiler-silla-de-ruedas-porto" element={<RegionalHubSEOPage region="porto" />} />
      <Route path="/fr/location-fauteuil-roulant-lisbonne" element={<RegionalHubSEOPage region="lisbon" />} />
      <Route path="/fr/location-fauteuil-roulant-algarve" element={<RegionalHubSEOPage region="algarve" />} />
      <Route path="/fr/location-fauteuil-roulant-porto" element={<RegionalHubSEOPage region="porto" />} />
      <Route path="/de/rollstuhl-mieten-lissabon" element={<RegionalHubSEOPage region="lisbon" />} />
      <Route path="/de/rollstuhl-mieten-algarve" element={<RegionalHubSEOPage region="algarve" />} />
      <Route path="/de/rollstuhl-mieten-porto" element={<RegionalHubSEOPage region="porto" />} />
      <Route path="/acessibilidade/:city" element={<CityAccessibilitySEOPage />} />
      <Route path="/en/accessibility/:city" element={<CityAccessibilitySEOPage />} />
      <Route path="/:code" element={<PartnerReferralRedirect />} />
      <Route path="*" element={<PageNotFound />} />
    </Routes>
    </Suspense>
    {isPublicRoute(window.location.pathname) && <CookieConsent />}
    </>
  );
};

const PUBLIC_PATHS = ['/start','/gestosageis','/parceiros','/privacy','/terms','/cookies','/about','/contact','/faq','/SearchResults','/Reviews','/obrigado','/guia','/mobilidade','/ortopedia','/ortopedia-benfica','/ortopedia-lisboa','/ortopedia-junqueira','/ortopedia-restelo','/comprar-cadeira-de-rodas','/comprar-cama-articulada','/comprar-andarilhos','/comprar-scooter-mobilidade','/loja-ortopedia-lisboa','/lojas','/aluguer-cadeira-de-rodas-eletrica','/connect','/contrato','/melhor-loja-ortopedia-lisboa','/POS','/POSSlideshowManagement'];
const isPublicRoute = (p) => PUBLIC_PATHS.includes(p) || p.startsWith('/ortopedia-') || p === '/ortopedia-junqueira' || p === '/ortopedia-restelo' || p === '/loja-ortopedia-lisboa' ||   p.startsWith('/aluguer/') || p.startsWith('/compra-material-ortopedico/') || p.startsWith('/compra/') || p.startsWith('/loja/') || p === '/aluguer-material-ortopedico-lisboa' || p.startsWith('/en/shop/') || p.startsWith('/es/tienda/') || p.startsWith('/fr/boutique/') || p.startsWith('/de/laden/') || p.startsWith('/en/rent/') || p.startsWith('/en/mobility') || p.startsWith('/es/alquilar/') || p.startsWith('/fr/louer/') || p.startsWith('/de/mieten/') || p.startsWith('/it/noleggiare/') || p.startsWith('/nl/huren/') || p.startsWith('/entrega/') || p.startsWith('/r/') ||     p.startsWith('/acessibilidade/') || p.startsWith('/en/accessibility/') || p.startsWith('/contrato/') || p === '/aluguer-cadeira-de-rodas' || p === '/aluguer-cama-articulada' || p === '/aluguer-andarilhos' || p === '/aluguer-scooter-mobilidade' || p === '/aluguer-cadeira-de-rodas-eletrica' || p === '/aluguer-cadeira-de-rodas-lisboa' || p === '/aluguer-cadeira-de-rodas-algarve' || p === '/aluguer-cadeira-de-rodas-porto' || p.startsWith('/en/wheelchair-rental-') || p.startsWith('/es/alquiler-silla-de-ruedas-') || p.startsWith('/fr/location-fauteuil-roulant-') || p.startsWith('/de/rollstuhl-mieten-') || p === '/ortopedia' || p === '/melhor-loja-ortopedia-lisboa' || p === '/comprar-cadeira-de-rodas' || p === '/comprar-cama-articulada' || p === '/comprar-andarilhos' || p === '/comprar-scooter-mobilidade' || p === '/lojas' || p === '/loja-ortopedia-benfica' || p === '/loja-ortopedia-junqueira' || p === '/' || p === '/Home' || p === '/Reservas' || p === '/Shop' || p === '/ShopCheckout' || p === '/ShopOrderSuccess' || p === '/Blog' || p === '/BlogPost' || p === '/LandingPage';


function App() {

  return (
    <AuthProvider>
      <LayoutProvider>
        <QueryClientProvider client={queryClientInstance}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <NavigationTracker />
          <PartnerReferralCapture />
          <Suspense fallback={<PageFallback />}>
          <Routes>
            <Route path="/SearchLogs" element={<LayoutWrapper currentPageName="SearchLogs"><SearchLogs /></LayoutWrapper>} />
      <Route path="/StripeSuccess" element={<StripeSuccess />} />
            <Route path="*" element={<AuthenticatedApp />} />
          </Routes>
          </Suspense>
          <Toaster />
          <VisualEditAgent />
        </Router>
      </QueryClientProvider>
      </LayoutProvider>
    </AuthProvider>
  )
}

export default App